import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Play, Pause, Volume2, VolumeX, Radio, Heart, Flame } from "lucide-react";
import { toast } from "sonner";
import useNowPlaying from "@/hooks/useNowPlaying";
import { getOrCreateSessionId } from "@/lib/visitorSession";
import { attachAudio, resumeContext } from "@/lib/audioAnalyzer";
import ShareCard from "@/components/ShareCard";

const STREAM_URL = "https://stream.radiojar.com/618fw7nphrwtv";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function trackKey(title, artist) {
  return `${(artist || "").trim().toLowerCase()}|${(title || "").trim().toLowerCase()}`;
}

/**
 * Custom-branded Radiojar mini player — matches DRR's brutalist black/red
 * aesthetic instead of Radiojar's orange iframe.
 *
 * Uses HTML5 <audio> against the public stream URL so we control all UI.
 * Now-playing track info comes from the shared useNowPlaying hook which
 * proxies the ICY stream metadata through /api/now-playing (the Radiojar
 * widget JSON endpoint is blocked by CORS from the browser).
 *
 * When ``nickname`` is provided (listener has joined The Pit), a heart
 * button appears next to the track so they can vote on the current spin
 * straight from the always-visible bottom bar — the top "Now Spinning"
 * strip used to host this control but listeners requested it live with
 * the player.
 */
export default function RadiojarPlayer({ nickname = "" }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const [loading, setLoading] = useState(false);
  const [errored, setErrored] = useState(false);
  const np = useNowPlaying();
  const nowPlaying = np?.ok && np.title ? { title: np.title, artist: np.artist || "" } : null;
  const trending = !!np?.trending;
  const hearts10m = Number(np?.hearts_10m || 0);

  // Listener's hearted track_keys — used to render the live track's heart
  // filled when they've already voted. Refreshed when the live track changes
  // so the state stays in sync across spins.
  const [favKeys, setFavKeys] = useState(() => new Set());
  useEffect(() => {
    let cancelled = false;
    const sid = getOrCreateSessionId();
    axios
      .get(`${API}/track-favorites/mine`, { params: { session_id: sid } })
      .then((res) => {
        if (cancelled) return;
        setFavKeys(new Set(res?.data?.track_keys || []));
      })
      .catch(() => {
        /* silent — heart just stays empty */
      });
    return () => {
      cancelled = true;
    };
  }, [np?.title, np?.artist]);

  const liveKey = nowPlaying ? trackKey(nowPlaying.title, nowPlaying.artist) : "";
  const liveFav = liveKey ? favKeys.has(liveKey) : false;

  const toggleFavorite = useCallback(async () => {
    if (!nowPlaying?.title) return;
    const tk = trackKey(nowPlaying.title, nowPlaying.artist);
    const sid = getOrCreateSessionId();
    const wasFav = favKeys.has(tk);
    setFavKeys((prev) => {
      const next = new Set(prev);
      if (wasFav) next.delete(tk);
      else next.add(tk);
      return next;
    });
    try {
      await axios.post(`${API}/track-favorite`, {
        title: nowPlaying.title,
        artist: nowPlaying.artist || "",
        session_id: sid,
        action: wasFav ? "remove" : "add",
        nickname: nickname || "",
      });
    } catch (e) {
      setFavKeys((prev) => {
        const next = new Set(prev);
        if (wasFav) next.add(tk);
        else next.delete(tk);
        return next;
      });
      toast.error("Couldn't save favorite");
    }
  }, [nowPlaying, favKeys, nickname]);

  // Keep audio element synced with volume / mute state
  useEffect(() => {
    if (!audioRef.current) return;
    audioRef.current.volume = volume;
    audioRef.current.muted = muted;
  }, [volume, muted]);

  // Listen for global play-request events fired by any CTA on the site
  // (e.g. Contest page "LISTEN LIVE"). Only starts playback — never pauses —
  // so external CTAs can't accidentally kill an already-playing stream.
  useEffect(() => {
    const handler = () => {
      if (!audioRef.current) return;
      if (audioRef.current.paused) {
        togglePlay();
      }
    };
    window.addEventListener("drr:play-request", handler);
    return () => window.removeEventListener("drr:play-request", handler);
  }, []);

  const togglePlay = async () => {
    const el = audioRef.current;
    if (!el) return;
    if (playing) {
      el.pause();
      setPlaying(false);
      return;
    }
    setLoading(true);
    setErrored(false);
    try {
      // Force fresh stream each play to avoid stale buffer / token expiry
      el.src = `${STREAM_URL}?cb=${Date.now()}`;
      await el.play();
      setPlaying(true);
      // Feed the audio element into the global analyser once playback has
      // actually started. AudioContext.createMediaElementSource must run in
      // response to a user gesture, so we do it here inside the click
      // handler. Subsequent calls are cheap no-ops (attachAudio is guarded).
      attachAudio(el);
      resumeContext();
    } catch (e) {
      setErrored(true);
      setPlaying(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="border-t border-zinc-800 bg-black px-3 py-3 flex flex-col gap-2"
      data-testid="radiojar-custom-player"
    >
      <div className="text-[10px] tracking-[0.3em] text-zinc-400 uppercase flex items-center justify-between">
        <span className="flex items-center gap-1.5">
          <Radio className="w-3 h-3 text-red-500" />
          //_bonus_stream
        </span>
        <span
          className={`flex items-center gap-1.5 ${
            playing ? "text-red-500" : "text-zinc-500"
          }`}
        >
          {playing ? (
            <>
              <span className="on-air-dot scale-75" /> LIVE
            </>
          ) : (
            "RADIOJAR"
          )}
        </span>
      </div>

      <div className="flex items-center gap-3">
        {/* Play / Pause */}
        <button
          onClick={togglePlay}
          disabled={loading}
          className="shrink-0 w-11 h-11 flex items-center justify-center bg-red-600 hover:bg-white hover:text-black text-white border border-zinc-700 transition-colors disabled:opacity-50"
          data-testid="radiojar-play-toggle"
          aria-label={playing ? "Pause stream" : "Play stream"}
        >
          {loading ? (
            <span className="text-xs animate-pulse">···</span>
          ) : playing ? (
            <Pause className="w-5 h-5" />
          ) : (
            <Play className="w-5 h-5" />
          )}
        </button>

        {/* Now playing */}
        <div className="flex-1 min-w-0">
          {errored ? (
            <div className="text-xs text-red-500 tracking-widest uppercase">
              //_stream_failed · tap_play_again
            </div>
          ) : nowPlaying?.title ? (
            <>
              <div
                className="font-display text-sm md:text-base text-zinc-50 truncate tracking-tight leading-tight"
                title={nowPlaying.title}
              >
                {nowPlaying.title}
              </div>
              {nowPlaying.artist && (
                <div
                  className="text-[11px] text-zinc-400 truncate tracking-wide"
                  title={nowPlaying.artist}
                >
                  {nowPlaying.artist}
                </div>
              )}
            </>
          ) : (
            <div className="text-xs text-zinc-500 tracking-widest uppercase">
              //_press_play_to_tune_in
            </div>
          )}
        </div>

        {/* Trending flame — lights up when the current track has picked up
            3+ hearts in the past 10 min. Real-time community proof the
            current spin is going off. */}
        {nowPlaying?.title && trending && (
          <div
            className="shrink-0 flex items-center gap-1 px-2 h-9 border border-orange-500 bg-orange-500/10 text-orange-400 text-[10px] tracking-[0.2em] uppercase font-display drr-flame-pulse"
            title={`Trending: ${hearts10m} heart${hearts10m === 1 ? "" : "s"} in the last 10 minutes`}
            data-testid="radiojar-trending-flame"
            data-hearts-10m={hearts10m}
          >
            <Flame className="w-3.5 h-3.5 drr-flame-flicker" strokeWidth={2.4} />
            <span>×{hearts10m}</span>
          </div>
        )}

        {/* Share button — one-click brutalist share card generator. Shown
            whenever there's a live track to share, no chat-join required
            (max virality = zero friction). */}
        {nowPlaying?.title && (
          <ShareCard
            title={nowPlaying.title}
            artist={nowPlaying.artist}
            compact
          />
        )}

        {/* Heart / vote — only when listener has joined chat AND we have
            a live track to vote on. Sits next to the song so it's the
            first thing listeners see when they enter The Pit. */}
        {nickname && nowPlaying?.title && (
          <button
            type="button"
            onClick={toggleFavorite}
            className={`shrink-0 w-9 h-9 flex items-center justify-center border transition-colors ${
              liveFav
                ? "text-red-500 border-red-600 bg-red-600/10"
                : "text-zinc-400 border-zinc-700 hover:text-red-500 hover:border-red-600"
            }`}
            title={liveFav ? "Unfavorite this track" : "Favorite this track"}
            aria-label={liveFav ? "Unfavorite" : "Favorite this track"}
            data-testid="radiojar-now-playing-heart"
          >
            <Heart
              className="w-4 h-4"
              strokeWidth={2.2}
              fill={liveFav ? "currentColor" : "none"}
            />
          </button>
        )}

        {/* Volume / Mute */}
        <button
          onClick={() => setMuted((m) => !m)}
          className="shrink-0 w-8 h-8 flex items-center justify-center text-zinc-300 hover:text-red-500 transition-colors"
          data-testid="radiojar-mute-toggle"
          aria-label={muted ? "Unmute" : "Mute"}
        >
          {muted || volume === 0 ? (
            <VolumeX className="w-4 h-4" />
          ) : (
            <Volume2 className="w-4 h-4" />
          )}
        </button>
      </div>

      {/* Volume slider */}
      <input
        type="range"
        min="0"
        max="1"
        step="0.02"
        value={muted ? 0 : volume}
        onChange={(e) => {
          const v = parseFloat(e.target.value);
          setVolume(v);
          if (v > 0 && muted) setMuted(false);
        }}
        className="drr-volume-range w-full h-1 accent-red-600 cursor-pointer"
        data-testid="radiojar-volume"
        aria-label="Volume"
      />

      <audio ref={audioRef} preload="none" playsInline crossOrigin="anonymous" />
    </div>
  );
}
